import { useState } from "react";
import { AlertTriangle, CheckCircle2, ArrowRight, Eye, Layers3, SearchCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const beforeRequirement = {
  title: "Draft PRD: Smart Rider Assignment",
  content: `Dispatchers need a faster way to assign riders.

The system should look at available riders and suggest the best one for each order. It should consider distance, availability, and priority.

Dispatchers should be able to accept the suggestion or choose another rider.`,
  issues: [
    "No definition of 'best rider'",
    "Missing fallback behavior when no rider is suitable",
    "No testable acceptance criteria",
    "Unclear how priority affects assignment",
  ],
  score: 42,
};

const afterRequirement = {
  title: "Reviewed Requirements: Smart Rider Assignment",
  content: `Goal: Recommend the best available rider for each incoming order while keeping dispatchers in control.

Core requirements:
- Rank eligible riders using availability, distance, workload, priority, and delivery risk
- Explain why each rider was recommended
- Let dispatchers accept, reject, or manually override the recommendation
- Show a clear fallback state when no suitable rider is available

Acceptance criteria:
- Offline, blocked, stale, or overloaded riders are excluded unless clearly marked as exceptions
- Every recommendation shows the top contributing factors
- Overrides preserve reason, timestamp, dispatcher, and final assignment history`,
  improvements: [
    "Decision criteria made explicit",
    "Fallback and override behavior clarified",
    "Acceptance criteria are testable",
    "Audit trail and edge cases included",
  ],
  score: 82,
};

export function BeforeAfterDemo() {
  const [activeTab, setActiveTab] = useState<"before" | "after">("before");

  return (
    <section className="relative overflow-hidden py-14 md:py-20">
      <div className="absolute top-[20%] right-[10%] h-[300px] w-[300px] rounded-full bg-primary/[0.04] blur-[120px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Eye className="h-3.5 w-3.5" />
            Review and refinement
          </div>
          <h2 className="mb-3 font-sans text-2xl font-bold md:text-3xl">
            From vague PRD text to clearer engineering input
          </h2>
          <p className="text-base text-muted-foreground">
            Reqqy is useful whether you start with a rough idea or an existing draft that needs a sharper review.
          </p>
        </div>

        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-full border border-border/50 glass p-1">
            <button
              onClick={() => setActiveTab("before")}
              className={cn(
                "rounded-full px-5 py-2 text-sm font-medium transition-all duration-300",
                activeTab === "before"
                  ? "bg-destructive/20 text-destructive shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Draft PRD
            </button>
            <button
              onClick={() => setActiveTab("after")}
              className={cn(
                "rounded-full px-5 py-2 text-sm font-medium transition-all duration-300",
                activeTab === "after"
                  ? "gradient-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              After Reqqy
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-4xl">
          <div
            className={cn(
              "rounded-xl border glass p-5 transition-all duration-500 md:p-6",
              activeTab === "before" ? "border-destructive/20" : "border-primary/20"
            )}
          >
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-base font-semibold">
                {activeTab === "before" ? beforeRequirement.title : afterRequirement.title}
              </h3>
              <div
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
                  activeTab === "before" ? "bg-destructive/20 text-destructive" : "bg-primary/20 text-primary"
                )}
              >
                {activeTab === "before" ? <AlertTriangle className="h-3.5 w-3.5" /> : <SearchCheck className="h-3.5 w-3.5" />}
                Readiness: {activeTab === "before" ? beforeRequirement.score : afterRequirement.score}%
              </div>
            </div>

            <div
              className={cn(
                "mb-4 rounded-lg border p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap",
                activeTab === "before" ? "border-destructive/10 bg-destructive/[0.03]" : "border-primary/10 bg-primary/[0.03]"
              )}
            >
              {activeTab === "before" ? beforeRequirement.content : afterRequirement.content}
            </div>

            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-medium">
                {activeTab === "before" ? (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                    Issues Reqqy should catch
                  </>
                ) : (
                  <>
                    <Layers3 className="h-3.5 w-3.5 text-primary" />
                    Improvements after review
                  </>
                )}
              </h4>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {(activeTab === "before" ? beforeRequirement.issues : afterRequirement.improvements).map((item) => (
                  <div
                    key={item}
                    className={cn(
                      "flex items-start gap-1.5 rounded-lg p-2.5 text-xs",
                      activeTab === "before" ? "bg-destructive/[0.05]" : "bg-primary/[0.05]"
                    )}
                  >
                    {activeTab === "before" ? (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-destructive" />
                    ) : (
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
                    )}
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {activeTab === "before" && (
            <div className="mt-5 flex justify-center">
              <button
                onClick={() => setActiveTab("after")}
                className="group flex items-center gap-2 text-primary transition-all duration-300 hover:text-primary/80"
              >
                <span className="text-xs font-medium">See reviewed version</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
